import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import { ProductDataFilenameConventionService } from 'src/app/services/product-data-filename-conventions/product-data-filename-convention.service';
import {
  FileExtension,
  ProductDataFilenameConvention,
  fileExtensionLabel,
  filenameIdentifierLabel,
} from 'src/app/models/product-data-filename-conventions/product-data-filename-convention';
import { CreateProductDataFilenameConventionDialogComponent } from './create-product-data-filename-convention-dialog.component';
import { UpdateProductDataFilenameConventionDialogComponent } from './update-product-data-filename-convention-dialog.component';

// Read-only settings card for an org's latest ProductDataFilenameConvention row.
// Empty state → stroked "Create" button that opens the Phase 7 dialog.
// Populated state → identifier + extensions + green edit FAB (Phase 8) + history icon (Phase 9).
@Component({
  selector: 'app-product-data-filename-convention-card',
  templateUrl: './product-data-filename-convention-card.component.html',
  styleUrls: ['./product-data-filename-convention-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class ProductDataFilenameConventionCardComponent implements OnInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);

  @Input() organizationId: string | null = null;

  latest: ProductDataFilenameConvention | null = null;
  loaded = false;

  readonly identifierLabel = filenameIdentifierLabel;
  readonly extensionLabel = fileExtensionLabel;

  constructor(
    private readonly service: ProductDataFilenameConventionService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.reload();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('organizationId' in changes && !changes['organizationId'].firstChange) {
      this.reload();
    }
  }

  get hasConvention(): boolean {
    return this.latest !== null;
  }

  get sampleFilenames(): string[] {
    if (this.latest === null) return [];
    const base = this.latest.filename.identifier.toLowerCase();
    return this.latest.filename.extensions.map(
      (ext: FileExtension) => `${base}.${ext.toLowerCase()}`,
    );
  }

  openCreateDialog(): void {
    if (this.organizationId === null) return;
    this.dialog
      .open(CreateProductDataFilenameConventionDialogComponent, {
        data: { organizationId: this.organizationId },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ProductDataFilenameConvention | undefined) => {
        if (result) this.reload();
      });
  }

  openUpdateDialog(): void {
    if (this.organizationId === null || this.latest === null) return;
    this.dialog
      .open(UpdateProductDataFilenameConventionDialogComponent, {
        data: { organizationId: this.organizationId, current: this.latest },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ProductDataFilenameConvention | undefined) => {
        if (result) this.reload();
      });
  }

  private reload(): void {
    if (this.organizationId === null) return;
    this.loaded = false;
    this.service
      .getLatest(this.organizationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((row: ProductDataFilenameConvention | null) => {
        this.latest = row;
        this.loaded = true;
        this.cdr.markForCheck();
      });
  }
}
